/**
 * Copy for the Users surface (SPEC §9; USR-1, USR-6).
 *
 * Sentence case throughout — `Eyebrow` and `Badge` apply the uppercase and the
 * wide tracking themselves, so writing it uppercase here would double it.
 *
 * `dateLocale` and the date format live here for the same reason they do in
 * `spaceList.copy.ts`: formatting is a presentation choice that changes with
 * language, and this file is the seam where that will be dealt with.
 */
export const USERS_COPY = {
  caption: 'Members of this agency',

  columns: {
    member: 'Member',
    status: 'Status',
    role: 'Role',
    lastActive: 'Last active',
    /**
     * A count, never a credential (USR-12). What model of authenticator an
     * officer carries is not their agency's business — USR-11 shows that to the
     * person it belongs to. This answers the only question an admin has.
     */
    passkeys: 'Passkeys',
    /**
     * The enrollment lock (USR-14) — a control an admin sets, not a value.
     *
     * "Enrollment" rather than "Email sign-in", which is what the API's own
     * refusals say: the toggle governs both halves of USR-14, and naming it
     * after the half a person notices first would make the passkey gate read
     * as a bug the day somebody hits it.
     */
    enrollment: 'Enrollment',
    actions: 'Actions',
  },

  /**
   * Action labels, and their in-flight twins — a label swap rather than a
   * spinner, matching the space list.
   *
   * The confirm labels say what will happen, per the rule the space list sets.
   * But they are deliberately **lighter than severing's**: USR-3 makes
   * revocation reversible and reactivation is one click away, so borrowing the
   * weight of "ends their access" would overstate it. Demoting an admin is
   * likewise reversible.
   */
  actions: {
    /**
     * USR-1/USR-4's trigger, moved into this column from its own (product
     * direction) — a member's grants are no longer shown as chips in the
     * table, only editable through the drawer this opens.
     */
    spaceAccess: 'Space Access',
    revoke: 'Revoke',
    revoking: 'Revoking…',
    confirmRevoke: 'Confirm: they lose access',
    reactivate: 'Reactivate',
    reactivating: 'Reactivating…',
    designate: 'Make admin',
    designating: 'Making admin…',
    demote: 'Remove admin',
    demoting: 'Removing…',
    confirmDemote: 'Confirm: removes their admin rights',
    /**
     * USR-11a rung 2. Confirms first, like revoke and demote and for the same
     * reason: it takes something away, and the something is somebody's ability
     * to sign in.
     */
    resetPasskeys: 'Reset passkeys',
    confirmReset: 'Reset and sign them out',
    resetting: 'Resetting…',
    cancel: 'Cancel',
    /** The generic fallback. A `last_admin` refusal shows the API's own line. */
    failed: 'That did not work. Reload and try again.',
    /**
     * The row overflow's trigger (FolderView's `RowActions` precedent) — up to
     * four standing acts behind one control instead of a row of buttons.
     * Named per row: several buttons reading "More actions" are indistinguishable
     * to a screen reader, and the row context that separates them is visual.
     */
    more: (name: string) => `More actions for ${name}`,
  },

  /**
   * USR-14's lock.
   *
   * **Two strings, and each names the act the press performs** — not the state
   * the control is in. This was one string, `Enrollment for ${name}`, and that
   * was right while the control was a `<Switch>`: a switch announces its own
   * state, so "Enrollment for Dep. R. Dupuis, on" read correctly where an
   * instruction would have fought the state read out beside it.
   *
   * A padlock announces nothing on its own, so the label has to carry the whole
   * sentence. Naming the act rather than the state is `PDF_VIEWER_COPY.zoom`'s
   * pattern (`toFitPage` is what pressing it *does*), and it is the safer half
   * of the ambiguity: a reader who mishears "Locked" as an instruction has
   * learned the state anyway, where one who mishears an instruction as a state
   * presses the wrong control on a security setting.
   *
   * Named per row on `actions.more`'s reasoning: several controls reading
   * "Enrollment" are indistinguishable to a screen reader, and the row context
   * that separates them is visual.
   */
  enrollment: {
    /** Shown while enrollment is unlocked — pressing re-locks it. */
    lock: (name: string) => `Lock enrollment for ${name}`,
    /** Shown while enrollment is locked — pressing opens it. */
    unlock: (name: string) => `Unlock enrollment for ${name}`,
    /** The hover, which states the position rather than the act. */
    lockedState: (name: string) => `Enrollment is locked for ${name}`,
    unlockedState: (name: string) => `Enrollment is unlocked for ${name}`,
  },

  status: {
    Invited: 'Invited',
    Active: 'Active',
    Revoked: 'Revoked',
  },

  /** Only ever rendered for an admin; a badge on every row would say nothing. */
  role: { admin: 'Admin' },

  grants: {
    title: 'Space access',
    /**
     * Says what a grant *is*, because the control cannot. PERM-1 makes per-space
     * the only scope there is, and PERM-1a adds the level over that scope — so
     * this line now has to name both, or an admin reads three options as three
     * kinds of thing rather than one setting with three positions.
     */
    intro: 'Choose which agency spaces this member can reach, and what they can do there',
    /** Renamed from "Shared spaces": this is a list of agencies, and "Spaces" is what the rest of the app calls them. */
    legend: 'Spaces',

    /**
     * PERM-1a's three positions, and the reason the option that cannot be used
     * is still shown.
     *
     * **`write` is rendered disabled on a space the agency itself cannot send
     * into, never hidden.** Hiding it would make the control two-state on some
     * rows and three-state on others with nothing on screen to explain the
     * difference, and an admin would never learn that read and write exists or
     * what would unlock it. `observer` is that explanation, and it names a real
     * act (CON-13, CON-20).
     *
     * **It named the wrong act until M14's sweep**, and the key it was under is
     * why the error survived. `receiveOnly: 'Read + write needs a two-way
     * connection.'` sent an admin to *the other agency* — true when a space was
     * a pair and one side's plan decided the other's ceiling. `agencyMayWrite`
     * is `holdsPlan(viewer)` and nothing else (ADR-0070), so the remedy is the
     * reader's **own** subscription; the old sentence described a purchase
     * nobody in the room could make on their behalf.
     */
    access: {
      /** Per-row accessible name. The visible agency name is the row's own text. */
      label: (agency: string) => `Access for ${agency}`,
      none: 'No access',
      read: 'Read only',
      write: 'Read + write',
      observer: 'Read + write needs an active subscription for your agency.',
    },
    close: 'Close space access',
    save: 'Save access',
    saving: 'Saving…',
    cancel: 'Cancel',

    /**
     * Only Active spaces can be granted: a `Requested` space has no space yet
     * (CON-6) and a `Severed` one has lost it (CON-14).
     */
    noSpaces: 'This agency has no active spaces yet. Connect an agency first.',

    /** The generic fallback. The API's own line wins where there is one. */
    failed: 'Those changes did not save. Try again.',
  },

  /**
   * Inviting a member (USR-2) — the only way anybody joins an agency.
   *
   * Two lines here are load-bearing. `emailHint` interpolates the agency's own
   * domain, so the rule is visible *before* it is broken rather than only in a
   * refusal. And `intro` says two things an admin would otherwise wait to find
   * out: that signing in is what activates the member (SYS-6), and that their
   * name arrives with them rather than being typed here (USR-2, ADR-0038) —
   * which is what makes the address in the Name column read as a fact rather
   * than a fault.
   */
  invite: {
    open: 'Invite member',
    title: 'Invite a member',
    intro:
      'Their account is created here — no email is sent. Tell them to go to this site, enter this address, and click Sign In; they become active, and their name appears here, the first time they do.',

    fields: {
      email: 'Email',
      /** A function, as `spaceRoot.copy.ts` does for its counts. */
      emailHint: (domain: string) => `Must be an address at ${domain}.`,
    },

    legend: 'Space access (optional)',
    /** Zero grants is a real state, and the grant editor is the way out of it. */
    noSpaces: 'This agency has no active spaces yet. You can grant access later.',

    submit: 'Invite member',
    submitting: 'Inviting…',
    cancel: 'Cancel',
    close: 'Close invite',

    errors: {
      emailRequired: 'Enter an email address.',
      /** Mirrors the API's rule; it is not a substitute for it (SEC-5). */
      wrongDomain: (domain: string) => `A member’s address must be at ${domain}.`,
      /** The generic fallback. The API's own line wins where there is one. */
      failed: 'That invitation did not send. Try again.',
    },
  },

  lastActive: { never: 'Never' },

  filters: {
    status: {
      label: 'Status',
      all: 'All statuses',
      Invited: 'Invited',
      Active: 'Active',
      Revoked: 'Revoked',
    },
    role: {
      label: 'Role',
      all: 'Everyone',
      admins: 'Admins',
      members: 'Members',
    },
  },

  states: {
    loading: 'Loading members…',
    empty: 'No members match these filters.',
    error: 'Members could not be loaded.',
    forbidden: 'This surface is available to agency admins.',
    retry: 'Try again',
  },

  dateLocale: 'en-US',
} as const;
